/**
 * FormsListPage
 *
 * Displays available public forms that the customer can fill out.
 * Links to individual form submission pages and submission history.
 *
 * Requirements: Task 12 - Forms Pages
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import {
  ClipboardList,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  ArrowRight,
} from '@bloomcode/icons';

import { usePublicForms } from '@/hooks/useForms';

/**
 * Empty state when no forms are available
 */
function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center">
        <ClipboardList className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Forms Available</h3>
        <p className="text-muted-foreground max-w-sm">
          When your account team publishes forms for you to fill out, they will appear here.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Pagination controls
 */
function PaginationControls({
  page,
  totalPages,
  hasNext,
  hasPrev,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function FormsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { forms, pagination, totalCount, isLoading, error, refetch } = usePublicForms({
    page,
    limit: 10,
  });

  // Loading state
  if (isLoading && forms.length === 0) {
    return (
      <>
        <Helmet>
          <title>Forms | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Forms</h1>
            <p className="text-muted-foreground">Submit forms and view your past submissions.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="animate-pulse space-y-3">
                    <div className="h-5 w-40 rounded bg-muted" />
                    <div className="h-4 w-full rounded bg-muted" />
                    <div className="h-4 w-24 rounded bg-muted" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error && forms.length === 0) {
    return (
      <>
        <Helmet>
          <title>Forms | Bloomcode CRM Portal</title>
        </Helmet>
        <div className="container mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-heading font-bold">Forms</h1>
          </div>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load forms</p>
              <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Forms | Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto p-6">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Forms</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Forms</h1>
            <p className="text-muted-foreground">
              {totalCount > 0
                ? `${totalCount} form${totalCount !== 1 ? 's' : ''} available`
                : 'Submit forms and view your past submissions.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/forms/submissions')}
              aria-label="View my submissions"
            >
              <FileText className="mr-2 h-4 w-4" />
              My Submissions
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              aria-label="Refresh forms"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Forms Grid or Empty State */}
        {forms.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2" role="list" aria-label="Available forms">
              {forms.map((form) => (
                <Card
                  key={form.id}
                  className="cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
                  onClick={() => navigate(`/forms/${form.id}`)}
                  role="listitem"
                  tabIndex={0}
                  aria-label={`Form: ${form.name}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/forms/${form.id}`);
                    }
                  }}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{form.name}</CardTitle>
                      <Badge variant="secondary">{form.submissionCount ?? 0} submissions</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {form.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {form.description}
                      </p>
                    )}
                    <div className="flex items-center text-sm text-primary font-medium">
                      Fill out form
                      <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <PaginationControls
                page={pagination.page}
                totalPages={pagination.pages}
                hasNext={pagination.hasNext}
                hasPrev={pagination.hasPrev}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
